import { useState, useEffect, useCallback } from 'react';
import { categorise } from './utils/categorise';
import { getAllItems, addDealToDefaultList } from './utils/shoppingLists';
import { BACKEND_ENABLED } from './config';
import { AuthProvider, useAuth } from './context/AuthContext';
import Header from './components/Header';
import DealGrid from './components/DealGrid';
import ShoppingList from './components/ShoppingList';
import AuthModal from './components/AuthModal';
import ProfilePage from './pages/ProfilePage';
import AdminPage from './pages/AdminPage';
import FavouritesPage from './pages/FavouritesPage';
import ForYouPage from './pages/ForYouPage';
import ComparePage from './pages/ComparePage';
import RecommendationsSection from './components/RecommendationsSection';
import FloatingAssistant from './components/FloatingAssistant';
import InstallPrompt from './components/InstallPrompt';
import './App.css';

const API = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

function AppInner() {
  const { user, loading: authLoading, getToken, recordBehaviour, toggleFavourite } = useAuth();
  const [activeTab, setActiveTab] = useState('Deals');
  const [deals, setDeals] = useState([]);
  const [updatedAt, setUpdatedAt] = useState(null);
  const [catalogues, setCatalogues] = useState({});
  const [loading, setLoading] = useState(true);
  const [scraping, setScraping] = useState(false);
  const [statusMsg, setStatusMsg] = useState('');
  const [shoppingList, setShoppingList] = useState(() => {
    try { return getAllItems(); } catch { return []; }
  });
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [bannerMsg, setBannerMsg] = useState('');
  const [favDismissed, setFavDismissed] = useState(false);
  const [recommendations, setRecommendations] = useState([]);
  const [recsLoading, setRecsLoading] = useState(false);

  // Handle URL params from OAuth callback and email verification
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('verified') === '1') {
      setBannerMsg('Email verified! Your account is ready.');
      window.history.replaceState({}, '', window.location.pathname);
    }
    if (params.get('auth_error')) {
      setBannerMsg('Google sign-in failed. Please try again.');
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  // When user logs in, switch away from the auth modal and back to Deals
  useEffect(() => {
    if (user) {
      setShowAuthModal(false);
      if (activeTab === 'Profile' && !user) setActiveTab('Deals');
    }
  }, [user, activeTab]);

  const fetchDeals = useCallback(async () => {
    // Deals come from a static snapshot (scraped on a schedule, published with the
    // frontend) so this works with no backend running at all. See .github/workflows/scrape.yml.
    try {
      const res = await fetch('/data/deals.json', { cache: 'no-store' });
      const data = await res.json();
      setDeals(data.deals || []);
      setUpdatedAt(data.updatedAt || null);
      setCatalogues(data.catalogues || {});
    } catch {
      setStatusMsg('Could not load deals snapshot.');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchRecommendations = useCallback(async () => {
    const token = getToken();
    if (!token) return;
    setRecsLoading(true);
    try {
      const res  = await fetch(`${API}/auth/recommendations`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      setRecommendations(data.recommendations || []);
    } catch {}
    finally { setRecsLoading(false); }
  }, [getToken]);

  const fetchScrapeStatus = useCallback(async () => {
    if (!BACKEND_ENABLED) return;
    try {
      const res = await fetch(`${API}/scrape-status`);
      const data = await res.json();
      setScraping(data.inProgress);
    } catch {}
  }, []);

  useEffect(() => { fetchDeals(); }, [fetchDeals]);

  useEffect(() => {
    if (user) fetchRecommendations();
    else setRecommendations([]);
  }, [user, fetchRecommendations]);

  // Refresh recommendations whenever deals update so scores reflect latest stock
  useEffect(() => {
    if (user && deals.length > 0) fetchRecommendations();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deals.length]);

  useEffect(() => {
    if (!BACKEND_ENABLED) return;
    const interval = scraping ? 2000 : 10000;
    const id = setInterval(() => {
      fetchScrapeStatus();
      if (!scraping) fetchDeals();
    }, interval);
    return () => clearInterval(id);
  }, [scraping, fetchDeals, fetchScrapeStatus]);

  useEffect(() => { fetchScrapeStatus(); }, [fetchScrapeStatus]);

  async function handleRefresh(stores = null) {
    if (scraping) return;
    setScraping(true);
    setStatusMsg('');
    try {
      const token = getToken();
      await fetch(`${API}/scrape`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: stores ? JSON.stringify({ stores }) : undefined,
      });
    } catch {
      setStatusMsg('Could not reach backend.');
      setScraping(false);
    }
  }

  function addToList(deal) {
    setShoppingList(addDealToDefaultList(deal));
    if (user) recordBehaviour({ ...deal, category: categorise(deal) });
  }


  const favourites   = user?.favourites || [];
  const favSet       = new Set(favourites.map(f => f.name + f.store));
  const favOnSaleNow = deals.filter(d => favSet.has(d.name + d.store));

  async function handleToggleFavourite(deal) {
    if (!user) { setShowAuthModal(true); return; }
    await toggleFavourite(deal.name, deal.store);
  }

  // Favouriting needs the backend — pass undefined (not a no-op) so the
  // heart button itself doesn't render when it can't do anything.
  const favouriteHandler = BACKEND_ENABLED ? handleToggleFavourite : undefined;

  if (authLoading) return null;

  return (
    <div className="app">
      <InstallPrompt />
      {bannerMsg && (
        <div className="banner banner--success">
          {bannerMsg}
          <button onClick={() => setBannerMsg('')}>✕</button>
        </div>
      )}

      {user && !user.verified && (
        <div className="banner banner--warning">
          <span>Please verify your email address — check your inbox for the verification link.</span>
        </div>
      )}

      {user && !favDismissed && favOnSaleNow.length > 0 && activeTab !== 'Favourites' && (
        <div className="banner banner--fav">
          <span>♥ {favOnSaleNow.length} favourite{favOnSaleNow.length !== 1 ? 's' : ''} {favOnSaleNow.length === 1 ? 'is' : 'are'} on sale right now!</span>
          <button className="banner-fav-view" onClick={() => { setActiveTab('Favourites'); setFavDismissed(true); }}>View deals</button>
          <button onClick={() => setFavDismissed(true)}>✕</button>
        </div>
      )}

      <Header
        activeTab={activeTab}
        onTabChange={tab => {
          if ((tab === 'Profile' || tab === 'Favourites' || tab === 'For You') && !user) { setShowAuthModal(true); return; }
          if (tab === 'Favourites') setFavDismissed(true);
          setActiveTab(tab);
        }}
        onRefresh={handleRefresh}
        onRefreshStore={store => handleRefresh([store])}
        scraping={scraping}
        lastUpdated={updatedAt}
        onSignIn={() => setShowAuthModal(true)}
        favOnSaleCount={user ? favOnSaleNow.length : 0}
      />

      <main className="main">
        {activeTab === 'Deals' && (
          <>
            {user && recommendations.length > 0 && (
              <RecommendationsSection
                recommendations={recommendations}
                shoppingList={shoppingList}
                favourites={favourites}
                onAddToList={addToList}
                onToggleFavourite={favouriteHandler}
                onSeeAll={() => setActiveTab('For You')}
              />
            )}
            <DealGrid
              deals={deals}
              shoppingList={shoppingList}
              onAddToList={addToList}
              loading={loading}
              message={statusMsg}
              catalogues={catalogues}
              favourites={favourites}
              onToggleFavourite={favouriteHandler}
            />
          </>
        )}
        {activeTab === 'Shopping List' && (
          <ShoppingList />
        )}
        {activeTab === 'Favourites' && user && (
          <FavouritesPage
            favourites={favourites}
            deals={deals}
            shoppingList={shoppingList}
            onAddToList={addToList}
            onToggleFavourite={favouriteHandler}
          />
        )}
        {activeTab === 'For You' && (
          <ForYouPage
            recommendations={recommendations}
            loading={recsLoading}
            shoppingList={shoppingList}
            favourites={favourites}
            onAddToList={addToList}
            onToggleFavourite={favouriteHandler}
            user={user}
            onSignIn={() => setShowAuthModal(true)}
          />
        )}
        {activeTab === 'Compare' && (
          <ComparePage
            shoppingList={shoppingList}
            onAddToList={addToList}
            favourites={favourites}
            onToggleFavourite={favouriteHandler}
          />
        )}
        {activeTab === 'Profile' && user && <ProfilePage />}
        {activeTab === 'Admin' && user?.isAdmin && <AdminPage />}
      </main>

      {showAuthModal && <AuthModal onClose={() => setShowAuthModal(false)} />}

      {BACKEND_ENABLED && !user?.isAdmin && (
        <FloatingAssistant shoppingList={shoppingList} onAddToList={addToList} onTabChange={setActiveTab} favourites={favourites} onToggleFavourite={favouriteHandler} />
      )}
    </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <AppInner />
    </AuthProvider>
  );
}

export default App;
